<template>
  <div ref="levelSlider" class="level-slider pb-8 pt-12">
    <div
      v-for="[lsLevel, lsXp] in renderedLevels"
      :key="lsLevel"
      class="level-slide bg-ui-panel d-flex align-center flex-column mx-8 px-4 py-3"
      :class="{
        'is-tier-upgrade py-4': lsLevel % config.tierUpgradeEvery === 0,
      }"
    >
      <AccountLevelsHeader
        :icon-size="48"
        :level="lsLevel"
        :tier="toTier(lsLevel)"
        :xp="lsXp" class="mb-2"
      />

      <ul v-if="rewards[lsLevel].length" class="pl-0 flex-grow-1 mb-2 green100--text text-caption">
        <li v-for="(reward, i) in rewards[lsLevel]" :key="i">
          <span v-text="$t(`page.account.level_rewards.type.${reward.type}`, reward.data)" />
        </li>
      </ul>
      <p v-else class="text-caption mb-0 text--secondary">
        No rewards
      </p>

      <span v-if="rewards[lsLevel].length" class="text-uppercase text-caption">
        <span v-if="claimedLevels.find(i => i === +lsLevel)" class="green400--text font-weight-bold">
          <fai :icon="['fas', 'check']" />
          Claimed
        </span>
        <span v-else-if="isLevelClaimable(lsLevel)" class="blue300--text font-weight-semibold">
          Unclaimed
        </span>
      </span>
    </div>

    <div
      v-if="!renderedMaxLevel"
      class="level-slide bg-ui-panel d-flex align-center justify-center flex-column mx-8 pa-4 py-6"
    >
      <fai
        fixed-width :icon="['fas', 'chevron-double-down']"
        rotation="270" class="blue200--text mb-4 text-h5"
      />
      <span class="text-caption font-weight-semibold text-center lh-1-4">
        Level up to see the next levels
      </span>
    </div>

    <v-btn
      fab class="slider-btn slider-btn--prev"
      :ripple="false" text x-small
      @click="flickity.previous()"
    >
      <fai fixed-width :icon="['fas', 'angle-down']" class="text-body-1" />
    </v-btn>
    <v-btn
      fab class="slider-btn slider-btn--next"
      :ripple="false" text x-small
      @click="flickity.next()"
    >
      <fai fixed-width :icon="['fas', 'angle-down']" class="text-body-1" />
    </v-btn>

    <v-btn
      fab class="slider-btn slider-btn--first"
      :ripple="false" text x-small
      @click="flickity.select(0, false, true)"
    >
      <fai fixed-width :icon="['fas', 'chevron-double-down']" class="text-caption" />
    </v-btn>
    <v-btn
      fab class="slider-btn slider-btn--last"
      :ripple="false" text x-small
      @click="flickity.select(renderedLevels.length - 1, false, true)"
    >
      <fai fixed-width :icon="['fas', 'chevron-double-down']" class="text-caption" />
    </v-btn>

    <v-btn
      class="slider-btn slider-btn--current"
      :ripple="false" fab text x-small
      @click="flickity.select(renderedLevelIndex)"
    >
      <fai fixed-width :icon="['fas', 'undo']" class="text-caption" />
    </v-btn>
  </div>
</template>

<script>
import Flickity from 'flickity'
import 'flickity/dist/flickity.min.css'

import { LEVELS } from '@/utils/constants'
import AccountLevelsHeader from './AccountLevelsHeader'

const MAX_RENDERED_LEVELS = 30

function getLevelUserRewards(level, config) {
  if (level < 1) return []

  const { maxTier, tierUpgradeEvery } = config
  const tier = Math.min(Math.floor(level / tierUpgradeEvery), maxTier)

  const tierOverrides = config.overrides.tiers?.[tier] ?? {}
  const levelOverrides = config.overrides.levels?.[level] ?? {}

  const overrides = {
    spins: tierOverrides.spins,
    spinsAmount: levelOverrides.spins,
    extras: [...(tierOverrides.extras ?? []), ...(levelOverrides.extras ?? [])],
  }

  const rewards = []

  if (level % tierUpgradeEvery === 0) {
    rewards.push({ type: 'tier_upgrade', data: { tier, level } })
  }

  // tier cases
  const tierLevel = tier * tierUpgradeEvery
  const tierProgress = (level - tierLevel) / tierUpgradeEvery

  const spinsConfig = {
    factor: overrides.spins?.factor ?? config.spins.factor,
    min: overrides.spins?.min ?? config.spins.min,
    max: overrides.spins?.max ?? config.spins.max,
  }

  const spinsFactor = spinsConfig.factor * (1 + tierProgress)
  let spins = Math.round(Math.min(Math.max(spinsFactor, spinsConfig.min), spinsConfig.max))

  if (overrides.spinsAmount) {
    spins = overrides.spinsAmount
  }

  if (spins) {
    rewards.push({
      type: 'level_case',
      data: { tier, level, amount: spins },
    })
  }


  // extra rewards
  if (overrides.extras.length) {
    rewards.push(
      ...overrides.extras.map(([extra, opts]) => ({
        type: extra,
        data: { tier, level, rewardOptions: opts },
      })),
    )
  }

  return rewards
}

export default {
  components: { AccountLevelsHeader },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    xp: {
      type: Number,
      default: 0,
    },
    config: {
      type: Object,
      required: true,
    },
    claimedLevels: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      flickity: null,
    }
  },
  computed: {
    level() {
      const nextLevel = LEVELS.findIndex(lvl => lvl > this.xp)

      // we return nextLevel because index starts at 0 and first level is 1
      return (nextLevel !== -1 ? nextLevel : LEVELS.length) - 1
    },
    tier() {
      return this.toTier(this.level)
    },
    maxTier() {
      return this.config.maxTier
    },
    renderedLevels() {
      const { level } = this

      const half = Math.round(MAX_RENDERED_LEVELS / 2)

      const start = Math.max(0, level - half)
      const end = Math.min(LEVELS.length, level + half)

      const levels = LEVELS.map((xp, i) => [i, xp])

      return levels.slice(start, end)
    },
    renderedLevelIndex() {
      const { renderedLevels, level } = this

      return renderedLevels.findIndex(([l]) => l === level)
    },
    renderedMaxLevel() {
      const [maxXp] = LEVELS.slice(-1)
      const [[, renderedMaxXp]] = this.renderedLevels.slice(-1)

      return maxXp === renderedMaxXp
    },
    rewards() {
      const { config } = this

      return LEVELS.map((xp, level) => {
        if (level === 0) return []

        const rewards = getLevelUserRewards(level, config)

        return rewards
      })
    },
    claimable() {
      const { claimedLevels, level } = this

      return Object.keys(LEVELS).map(i => +i).filter(i => {
        if (i === 0 || i > level) return false
        return !claimedLevels.includes(i)
      })
    },
  },
  mounted() {
    const { levelSlider: $levelSlider } = this.$refs

    this.flickity = new Flickity($levelSlider, {
      cellAlign: 'center',
      cellSelector: '.level-slide',
      initialIndex: this.renderedLevelIndex,
      prevNextButtons: false,
      pageDots: false,
      wrapAround: false,
    })
  },
  beforeDestroy() {
    if (this.flickity) {
      this.flickity.destroy()
    }
  },
  methods: {
    toTier(level) {
      return Math.min(Math.floor(level / this.config.tierUpgradeEvery), this.maxTier)
    },
    isLevelClaimable(level) {
      return this.claimable.includes(+level)
    },
  },
}
</script>

<style lang="scss" scoped>
.level-slider {
  overflow: hidden;
  position: relative;
  outline: none;
  background: url('../../../../assets/img/techtree.jpg') no-repeat center center;
  background-size: cover;

  &::before {
    position: absolute;
    z-index: 0;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(grey(800), 0.75);
    content: ' ';
    // pointer-events: none;
  }

  &::after {
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(transparent 50%, rgba(grey(900), 0.8) 80%);
    content: ' ';
    pointer-events: none;
  }

  ::v-deep .flickity-viewport {
    overflow: visible;
    transition: height 0.2s ease;
  }

  .slider-btn {
    position: absolute;
    z-index: 2;
    bottom: 8px;

    &--prev {
      left: 48px;
      transform: rotate(90deg);
    }

    &--next {
      right: 48px;
      transform: rotate(-90deg);
    }

    &--first {
      left: 12px;
      transform: rotate(90deg);
    }

    &--last {
      right: 12px;
      transform: rotate(-90deg);
    }

    &--current {
      left: 50%;
      transform: translateX(-50%);
    }
  }
}

.header-level {
  box-shadow: 0 0 30px rgba(green(400), 0.1), 0 0 20px rgba(green(400), 0.4);
}

.level-slide {
  position: relative;
  z-index: 1;
  width: 164px;
  transition: transform 0.3s ease;

  &.is-tier-upgrade {
    // background: grey(600);
  }

  &.is-hidden {
    visibility: hidden;
    opacity: 0 !important;
  }

  ul {
    list-style-type: none;
  }

  ul > li::before {
    position: relative;
    margin-right: 0.5em;
    content: '-'; /* en dash */
  }

  &::v-deep {
    &:not(.is-tier-upgrade) {
      .tier-icon {
        // display: none;
        width: 40px !important;
        margin-top: -24px;
      }
    }

    .tier-icon {
      margin-top: -32px;
      margin-bottom: 8px;
      // background-color: grey(700) !important;
    }

    .level {
      font-size: 14px !important;
      line-height: 1.2;
      white-space: nowrap;
    }

    .xp {
      font-size: 10px !important;
      white-space: nowrap;
    }
  }

  &:hover {
    transform: translateY(-3px);
  }
}
</style>
